import { useMemo } from 'react';
import {
  CompetitionGenderEnum,
  EventCompetitivenessEnum,
  EventPrivacyEnum,
  type FindGameEventFieldsFragment,
  type FindGameRosterFieldsFragment,
  type GeographyComparisonExp,
  PlaySessionMatchCompetitivenessEnum,
  SportsEnum,
  VenueNetsEnum,
  useGetFindGamesQuery,
  useGetFindGamesWithRosterQuery,
} from 'types/generated/client';
import { getVerificationLevel } from 'types/verification';
import { buildGameTitle } from 'utils/shared/games/buildGameTitle';
import getFirstNameLastInitial from 'utils/shared/string/getFirstNameLastInitial';
import { getCurrentSessionWindow } from 'utils/shared/time/getEventSchedule';
import { resolveVenueTimeZone } from 'utils/shared/time/venueDayFilters';
import { getProfileImageUrlOrPlaceholder } from 'utils/shared/user/getProfileImageUrlOrPlaceholder';
import { getAccessParamters } from 'utils/shared/venues';
import { useViewer } from 'hooks/useViewer';
import { GameFormat, getGameFormat, getPlayersPerTeam } from 'components/Find/gameFormat';
import { mapCompetitiveness } from 'components/Find/mapCompetitiveness';
import { deriveCourtTypes } from './filters';
import { type LatLng, distanceMilesTo } from './geo';
import type { FindGame, FindGameAudience, FindGamePlayer, FindVenueDetails } from './types';

/** The roster half is optional: a logged-out viewer's query never selects it. */
type FindGameEventRow = FindGameEventFieldsFragment & Partial<FindGameRosterFieldsFragment>;
type FindGameGroup = FindGameEventRow['groups'][number];

const mapAudience = (
  gender: CompetitionGenderEnum,
): { audience: FindGameAudience; label: string } => {
  if (gender === CompetitionGenderEnum.Male) return { audience: 'MALE', label: 'Men’s match' };
  if (gender === CompetitionGenderEnum.Female)
    return { audience: 'FEMALE', label: 'Women’s match' };
  if (gender === CompetitionGenderEnum.Mixed)
    return { audience: 'MIXED', label: 'Mixed doubles match' };
  if (gender === CompetitionGenderEnum.Coed) return { audience: 'COED', label: 'Co-ed match' };
  return { audience: 'ALL', label: 'For all players' };
};

const buildVenueDetails = (event: FindGameEventRow): FindVenueDetails => {
  const venue = event.venue;
  const name = venue?.title || event.displayLocation || event.addressString || '';
  const netLabel =
    venue?.pickleballNets === VenueNetsEnum.Permanent ||
    venue?.pickleballNets === VenueNetsEnum.Tennis
      ? 'Permanent'
      : 'Portable';

  return {
    imageUrl: venue?.images?.[0]?.url,
    locationLabel: name,
    name,
    accessLabel: getAccessParamters(venue?.accessType)?.name ?? '',
    courtCount: venue ? venue.indoorCourtCount + venue.outdoorCourtCount : 0,
    netLabel,
    fullAddress: venue?.addressString || event.addressString || event.displayLocation || '',
  };
};

const buildHostPlayer = (event: FindGameEventRow, group: FindGameGroup): FindGamePlayer => {
  const profile = event.hostUserProfile;
  const displayName = profile?.preferredName || profile?.fullName || 'Host';
  const profileSkillLevel =
    event.sport === SportsEnum.Tennis
      ? profile?.tennisSkillLevel?.id
      : event.sport === SportsEnum.Pickleball
      ? profile?.pickleballSkillLevel?.id
      : undefined;
  return {
    id: profile?.id ?? event.id,
    name: getFirstNameLastInitial(displayName),
    imageUrl: getProfileImageUrlOrPlaceholder({ path: profile?.profileImagePath }),
    verificationLevel: getVerificationLevel({
      identityVerifications: profile?.identityVerifications,
    }),
    isHost: true,
    // Real skill only: the game's self-rated level, else the host's profile level for
    // the sport. No pill when neither is known — never a fabricated default.
    skillLevel: group.selfRatedMinSkillLevel?.id ?? profileSkillLevel ?? undefined,
  };
};

/** Confirmed players other than the host, in the order they were accepted. */
const buildAcceptedPlayers = (event: FindGameEventRow, group: FindGameGroup): FindGamePlayer[] =>
  event.acceptedPlayRequests.flatMap((request) => {
    const profile = request.requesterProfile;
    if (!profile) return [];

    const displayName = profile.preferredName || profile.fullName || 'Player';
    const profileSkillLevel =
      event.sport === SportsEnum.Tennis
        ? profile.tennisSkillLevel?.id
        : event.sport === SportsEnum.Pickleball
        ? profile.pickleballSkillLevel?.id
        : undefined;

    return [
      {
        id: profile.id,
        name: getFirstNameLastInitial(displayName),
        imageUrl: getProfileImageUrlOrPlaceholder({ path: profile.profileImagePath }),
        verificationLevel: getVerificationLevel({
          identityVerifications: profile.identityVerifications,
        }),
        isHost: false,
        skillLevel: profileSkillLevel ?? group.selfRatedMinSkillLevel?.id ?? undefined,
      },
    ];
  });

/** Maps one events row (with its single group) to the card/modal `FindGame` shape. */
export const mapEventToFindGame = (
  event: FindGameEventRow,
  viewerCenter: LatLng | null,
  searchCenter: LatLng | null,
): FindGame | null => {
  const group = event.groups[0];
  // The query already restricts to player-listed games; this only skips a half-created one.
  if (!group) return null;

  const courtCostPerPlayer =
    event.courtCostPerPlayerUnitAmount && event.courtCostPerPlayerUnitAmount > 0
      ? event.courtCostPerPlayerUnitAmount / 100
      : undefined;
  const { audience, label: audienceLabel } = mapAudience(group.gender);
  const latitude = event.latitude != null ? Number(event.latitude) : undefined;
  const longitude = event.longitude != null ? Number(event.longitude) : undefined;
  const venue = buildVenueDetails(event);
  const host = buildHostPlayer(event, group);
  const accepted = buildAcceptedPlayers(event, group);
  const format = getGameFormat(group.teamType);
  const participantLimit = event.participantLimit ?? undefined;
  // Open Play is one roster whose capacity is the head count; the others are two sides.
  const playersPerTeam =
    getPlayersPerTeam(format) ?? Math.max(participantLimit ?? 0, 1 + accepted.length);
  // Total seats incl. host; null = unlimited Open Play (never full).
  const capacity = format === GameFormat.OpenPlay ? participantLimit ?? null : playersPerTeam * 2;
  const isFull = capacity != null && 1 + accepted.length >= capacity;
  const teams =
    format === GameFormat.OpenPlay
      ? [[host, ...accepted]]
      : // Host anchors side one; accepted players fill the remaining seats left to right.
        // Nothing records which pair someone is on, so this is a display order only.
        [[host, ...accepted.slice(0, playersPerTeam - 1)], accepted.slice(playersPerTeam - 1)];

  return {
    id: event.id,
    // The host's profile id IS their user id — the play-request recipient. No
    // fallback to event.id here (unlike buildHostPlayer): a missing host means we
    // can't safely target a request, so leave it undefined and disable the CTA.
    hostUserId: event.hostUserProfile?.id,
    sport: event.sport,
    competitiveness: mapCompetitiveness(group.competitiveness),
    title: buildGameTitle(group),
    ...getCurrentSessionWindow(event),
    priceFrom: courtCostPerPlayer,
    venueId: event.venue?.id ?? undefined,
    timezone: resolveVenueTimeZone(event),
    venueName: venue.name,
    distanceMiles: distanceMilesTo(viewerCenter, { latitude, longitude }),
    searchDistanceMiles: distanceMilesTo(searchCenter, { latitude, longitude }),
    isCourtBooked: event.isCourtBooked ?? false,
    isInviteOnly: event.privacy === EventPrivacyEnum.Private,
    // Undefined rather than false when the field wasn't selected at all (the
    // logged-out query), so "not requested" and "unknowable" stay distinguishable.
    hasPendingRequest: event.viewerRequests ? event.viewerRequests.length > 0 : undefined,
    latitude,
    longitude,
    format,
    playersPerTeam,
    participantLimit,
    isFull,
    teamType: group.teamType,
    gender: group.gender,
    // The posting's stated level, else the host's profile level for the sport (same
    // rule as the host player pill) — the best real skill signal for the game.
    skillLevel: host.skillLevel,
    courtTypes: deriveCourtTypes(
      event.venue?.indoorCourtCount ?? 0,
      event.venue?.outdoorCourtCount ?? 0,
    ),
    teams,
    details: {
      organizer: {
        name: event.hostUserProfile?.fullName || event.hostUserProfile?.preferredName || 'Host',
        imageUrl: getProfileImageUrlOrPlaceholder({
          path: event.hostUserProfile?.profileImagePath,
        }),
      },
      audience,
      audienceLabel,
      description: event.description,
      venue,
      courtCostPerPlayer,
      paymentMethod: 'Off platform',
    },
  };
};

/**
 * Live published pickup games for the /find "Games" tab, mapped to `FindGame`.
 * `areas` (from `searchAreas`) is where to look; `viewerCenter` only drives the
 * per-game distance and may be null.
 */
export const useFindGames = (
  viewerCenter: LatLng | null,
  areas: GeographyComparisonExp[],
  searchCenter: LatLng,
) => {
  const { userId, isUserSession, isSessionLoading } = useViewer();
  const now = useMemo(() => new Date().toISOString(), []);

  const area = useMemo(() => ({ _or: areas.map((geometry) => ({ geometry })) }), [areas]);
  const variables = { area, now };

  // Two documents, one of them always skipped. `anonymous` has no select permission
  // on play_requests, so the roster fragment isn't merely empty for a logged-out
  // viewer — the field doesn't exist in their schema and the operation fails
  // validation. Waiting out `isSessionLoading` keeps this to a single request rather
  // than firing the anonymous one and then re-firing with the roster.
  const isReady = !isSessionLoading;

  // Revalidate on mount: a game listed a moment ago must show when the host returns here.
  const anonymous = useGetFindGamesQuery({
    variables,
    skip: !isReady || isUserSession,
    fetchPolicy: 'cache-and-network',
  });
  const withRoster = useGetFindGamesWithRosterQuery({
    variables: { ...variables, viewerUserId: userId ?? '' },
    skip: !isReady || !isUserSession || !userId,
    fetchPolicy: 'cache-and-network',
  });

  const { data, loading } = isUserSession ? withRoster : anonymous;

  const games = useMemo<FindGame[]>(
    () =>
      (data?.events ?? [])
        .map((event) => mapEventToFindGame(event, viewerCenter, searchCenter))
        .filter((game): game is FindGame => game !== null),
    [data, viewerCenter, searchCenter],
  );

  // Not ready = the session hasn't settled, so neither query has run yet. Keep
  // loading rather than flashing "0 games" before it can.
  return { games, isLoading: (loading && !data) || !isReady };
};
